import { useState } from "react";

import { IconButton } from "ui/icon-button";
import { InlineEditableField, type InlineEditableFieldProps } from "ui/inline-editable-field";
import { Tooltip } from "ui/tooltip";
import toast from "components/partials/toast/toast";

import { useCurrentUser } from "hooks/api/use-current-user";
import { useSetIterableApiKey } from "hooks/api/use-clients";

import { hasPermission } from "utilities/user";
import { getErrorMessage } from "utilities";

import { Permission } from "types/auth";

import Icons from "assets";

interface MaskedInputProps
  extends Omit<
    InlineEditableFieldProps,
    | "value"
    | "onChange"
    | "onSave"
    | "onCancel"
    | "isEditing"
    | "isSaving"
    | "canSave"
    | "readActions"
  > {
  isLocked?: boolean;
  incomingValue?: string;
  clientId: string;
}

/**
 * The Iterable API key field on the legacy client edit form (`components/basic-info`): the value
 * arrives already masked from the API, and editing it goes through its own `useSetIterableApiKey`
 * mutation rather than the form's submit, behind `PERM_CLIENT_ADVANCED`.
 *
 * The College profile page has its own take on the same key
 * (`CollegeProfileIterableApiKeyInput`) — that one opens the edit by focusing the field, this one
 * by an Edit button in the field's border.
 */
export const MaskedInput = ({
  isLocked,
  incomingValue,
  clientId,
  ...rest
}: MaskedInputProps) => {
  const currentUser = useCurrentUser();
  const setIterableApiKeyMutation = useSetIterableApiKey();

  const hasEditPermission = hasPermission(currentUser, Permission.PERM_CLIENT_ADVANCED);

  const [value, setValue] = useState(incomingValue);
  const [isEditable, setIsEditable] = useState(!isLocked);

  function handleChange(event: React.ChangeEvent<HTMLInputElement>) {
    setValue(event.target.value);
  }

  function handleEdit() {
    setValue("");
    setIsEditable(true);
  }

  function handleCancel() {
    setValue(incomingValue);
    setIsEditable(false);
  }

  async function handleSave() {
    if (!value) return;

    try {
      await setIterableApiKeyMutation.mutateAsync({ id: clientId, iterableApiKey: value });
      toast.success({
        data: { title: "Updated Iterable API key" },
      });
      setIsEditable(false);
    } catch (error) {
      toast.error({
        data: {
          title: "Failed to save Iterable API key",
          message: getErrorMessage(error?.response?.data),
        },
      });
    }
  }

  return (
    <InlineEditableField
      {...rest}
      value={value}
      placeholder={incomingValue}
      onChange={handleChange}
      disabled={isLocked && !isEditable}
      isEditing={hasEditPermission && isEditable}
      readActions={
        hasEditPermission ? (
          <Tooltip label="Edit Iterable API Key">
            <IconButton
              aria-label="Edit Iterable API Key"
              size="medium"
              color="inherit"
              variant="text"
              onClick={handleEdit}>
              <Icons.Edit />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
      onSave={handleSave}
      onCancel={handleCancel}
      canSave={!!value}
      isSaving={setIterableApiKeyMutation.isPending}
    />
  );
};
